
import { ApiResponse, PublicData, ConfigEntry, Booking } from '../types';
import { LS_SCRIPT_URL_KEY, LS_AUTH_KEY, DEFAULT_SCRIPT_URL, CONFIG_CSV_URL, BOOKINGS_CSV_URL } from '../constants';

const getScriptUrl = () => localStorage.getItem(LS_SCRIPT_URL_KEY) || DEFAULT_SCRIPT_URL;

const FIELD_MAP: Record<string, string> = {
  'id': 'id', 'ID': 'id', '編號': 'id',
  'name': 'name', '姓名': 'name', '稱呼': 'name',
  'email': 'email', '電子郵件': 'email', '聯絡電話': 'email', '電話': 'email', '手機': 'email',
  'date': 'date', '日期': 'date', '預約日期': 'date',
  'time': 'time', '時間': 'time', '時段': 'time',
  'topic': 'topic', '主題': 'topic', '諮詢項目': 'topic',
  'description': 'description', '備註': 'description', '說明': 'description',
  'status': 'status', '狀態': 'status', '審核狀態': 'status',
  'createdAt': 'createdAt', '時間戳記': 'createdAt'
};

const normalizeDate = (dateStr: any): string => {
  if (!dateStr) return '';
  let clean = dateStr.toString().replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
  let normalized = clean.replace(/[\/\.]/g, '-').split(/[\sT]/)[0];
  let parts = normalized.split('-');
  
  if (parts.length === 3) {
    let y, m, d;
    if (parts[0].length === 4) { [y, m, d] = parts; }
    else if (parts[2].length === 4) { y = parts[2]; m = parts[0]; d = parts[1]; }
    else { y = parts[0].length === 2 ? '20' + parts[0] : parts[0]; m = parts[1]; d = parts[2]; }
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return normalized;
};

const parseCsv = (csv: string, name: string): any[] => {
  if (!csv) return [];
  const result = [];
  let currentField = '';
  let inQuotes = false;
  let currentRow = [];
  
  for (let i = 0; i < csv.length; i++) {
    const char = csv[i];
    const nextChar = csv[i + 1];
    if (inQuotes) {
      if (char === '"' && nextChar === '"') { currentField += '"'; i++; }
      else if (char === '"') { inQuotes = false; }
      else { currentField += char; }
    } else {
      if (char === '"') { inQuotes = true; }
      else if (char === ',') { currentRow.push(currentField.trim()); currentField = ''; }
      else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        currentRow.push(currentField.trim());
        result.push(currentRow);
        currentRow = [];
        currentField = '';
        if (char === '\r') i++;
      } else { currentField += char; }
    }
  }
  if (currentField || currentRow.length > 0) { currentRow.push(currentField.trim()); result.push(currentRow); }
  if (result.length < 2) return [];

  const rawHeaders = result[0].map((h: string) => h.replace(/[\u200B-\u200D\uFEFF]/g, '').trim());
  const headers = rawHeaders.map(h => FIELD_MAP[h] || h); 

  const finalData = result.slice(1).map(row => {
    return headers.reduce((acc: any, header: string, index: number) => {
      let val = (row[index] || '').toString().trim();
      if (header === 'email' && /^[9]\d{8}$/.test(val)) val = '0' + val;
      if (header === 'date') val = normalizeDate(val);
      if (header === 'status') val = val.toUpperCase() || 'PENDING';
      acc[header] = val;
      return acc;
    }, {} as any);
  });
  return finalData;
};

export const apiRequest = async <T,>(
  action: string,
  method: 'GET' | 'POST' = 'GET',
  body: any = null,
  keyOverride?: string
): Promise<ApiResponse<T>> => {
  if (action === 'getPublic') {
    try {
      const ts = Date.now();
      const [configRes, bookingRes] = await Promise.all([
        fetch(`${CONFIG_CSV_URL}&t=${ts}`).then(r => r.text()),
        fetch(`${BOOKINGS_CSV_URL}&t=${ts}`).then(r => r.text())
      ]);
      return { success: true, data: { configs: parseCsv(configRes, 'Configs'), bookings: parseCsv(bookingRes, 'Bookings') } as any as T };
    } catch (e) {
      return { success: false, message: '數據讀取失敗' };
    }
  }

  const url = getScriptUrl();
  const auth = (keyOverride !== undefined ? keyOverride : (localStorage.getItem(LS_AUTH_KEY) || '')).trim();

  try {
    const params = new URLSearchParams({ action });
    if (auth) params.append('adminKey', auth);

    const options: RequestInit = {
      method,
      mode: 'cors',
      redirect: 'follow', // 確保跟隨 GAS 的 302 重導向
    };

    if (method === 'POST') {
      options.headers = { 'Content-Type': 'text/plain' }; // 嚴格使用 text/plain 避免 CORS Preflight
      const payload = { ...body };
      if (payload.email) payload.email = String(payload.email).trim();
      if (auth) payload.adminKey = auth;
      options.body = JSON.stringify(payload);
    }

    const response = await fetch(`${url}?${params.toString()}`, options);
    const text = await response.text();
    
    // 自動偵測是否為 Google 登入頁面 (HTML)
    if (text.trim().startsWith('<')) {
      console.error('GAS returned HTML instead of JSON. Likely permission issue.');
      return { success: false, message: 'GAS 權限錯誤：請確認部署設定為「所有人 (Anyone)」' };
    }

    try {
      return JSON.parse(text);
    } catch (parseError) {
      console.error('GAS Response Body:', text);
      return { success: false, message: '伺服器回傳格式錯誤，請檢查 GAS 程式碼' };
    }
  } catch (error) {
    console.error('Network Error:', error);
    return { success: false, message: '網路連線異常，可能為跨網域 (CORS) 阻擋或網址錯誤' };
  }
};
